import React, { useRef } from "react";
import { useRouter } from "next/router";

import { getBrands } from "api/requests/getBrands";
import { Brand } from "custom-types/Brand";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import { getFirstQueryParam } from "utils/nextRouterUtils";
import { getCountryCodeHeader } from "utils/requestContext";

import BrandCoins from "components/brands/BrandCoins";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import PageNavigationV2 from "components/PageNavigationV2";
import ShopNoResults from "components/ShopNoResults";

import BrandListHeader from "../../components/brands/BrandListHeader";
import { HeaderComponentRef } from "../../components/brands/BrandListHeader/BrandListHeader";
import MetaTags from "../../components/MetaTags";

export type BrandAllPageProps = {
  currentPage: number;
  initialBrands: Array<Brand>;
  searchQuery: string | null;
  totalCount: number;
};

const PER_PAGE = 60;

const BrandAllPage: React.FC<BrandAllPageProps> = ({
  currentPage,
  initialBrands,
  searchQuery,
  totalCount,
}) => {
  const listHeaderComponentRef = useRef<HeaderComponentRef | null>(null);
  const resetSharedComponent = () => {
    listHeaderComponentRef.current?.setSearch();
  };

  const router = useRouter();
  const pageCount = Math.ceil(totalCount / PER_PAGE);

  return (
    <>
      <MetaTags
        canonicalParams={["page"]}
        description="Find more information about established and emerging weed brands, CDB brands, cannabis growers, cart makers, and more on Leafly."
        title="All Weed and CBD Brands on Leafly"
        robots={searchQuery ? "noindex" : undefined}
      />
      <Breadcrumbs>
        <Breadcrumb href="/brands" currentLocation>
          Brands
        </Breadcrumb>
      </Breadcrumbs>
      <BrandListHeader
        H1Text="All weed and CBD brands"
        searchQuery={searchQuery}
        ref={listHeaderComponentRef}
      />
      <div className="container">
        {initialBrands.length === 0 && (
          <div className="mt-6 mb-8">
            <ShopNoResults
              action={() => {
                router.push({});
                resetSharedComponent();
              }}
              subtitleText="Try searching another brand name or request this brand on Leafly"
              primaryLinkText="back to all brands"
              secondaryLink={{
                href: "https://success.leafly.com/brand-subscription",
                text: "request brand",
              }}
              titleText="No results found"
            />
          </div>
        )}
        {initialBrands.length > 0 && (
          <>
            <BrandCoins initialBrands={initialBrands} />
            <PageNavigationV2
              className="mt-8"
              currentPage={currentPage}
              totalPages={pageCount}
            />
            <div className="text-center mt-8 mb-section">
              <>
                Find information about all of the different weed farms, indoor
                cannabis growers, weed pen brands, CBD gummy makers, and more.
                Use Leafly to find a comprehensive list of weed and CBD brands
                and manufacturers operating in the US and Canada.
              </>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<BrandAllPageProps>(
  async (context) => {
    const countryCode = getCountryCodeHeader(context.req.headers);
    const { page, search } = context.query;
    const currentPage = Number(page || 1);

    const toSkip = (currentPage - 1) * PER_PAGE;

    const searchQuery = getFirstQueryParam(context.query.search);

    const searchOrSort = search
      ? { searchTerm: searchQuery }
      : { sort: { "sort[][name]": "asc" as const } };

    const allBrandsPageResponse = await getBrands({
      countryCode,
      skip: toSkip,
      take: PER_PAGE,
      ...searchOrSort,
    });

    return {
      props: {
        currentPage: currentPage,
        initialBrands: allBrandsPageResponse.data,
        searchQuery: searchQuery || null,
        totalCount: allBrandsPageResponse.metadata.totalCount,
      },
    };
  },
);

export default BrandAllPage;
